import React from 'react'
import styled from 'styled-components'
import BuyButton from '../../MwPage/components/BuyButton'
import { useRevealProps } from '../../MwPage/components/Reveal'
import RichText from '../../MwPage/components/RichText'
import { fillOffer } from '../../MwPage/offer'
import {
  cyan,
  DotGrid,
  Eyebrow,
  Hi,
  ice2,
  ink,
  Lede,
  onDark2,
  sans,
  Section,
  SectionHead,
  sky,
  Small,
  Stack,
} from '../../MwPage/styled'
import { useFocus } from '../focus'
import { Bullets, Card, Column, DarkPanel, Fig, FigNote, FigTitle, H2, Kicker, Note, PanelInner, Wrap } from '../styled'

const Chart = styled.svg`
  display: block;
  width: 100%;
  max-width: 440px;
  height: auto;
  margin: 0 auto;
`

const Closer = styled.p`
  margin: 0;
  font: 800 22px/1.25 ${sans};
  letter-spacing: -0.03em;
  color: ${onDark2};

  em {
    font-style: normal;
    color: #ffffff;
    text-decoration: underline;
    text-decoration-color: #a8d6fb;
    text-decoration-thickness: 0.12em;
    text-underline-offset: 0.15em;
  }
`

const AfterTitle = styled.h3`
  margin: 8px 0 0;
  font: 700 20px/1.3 ${sans};
  letter-spacing: -0.015em;
  color: ${ink};
`

const Intro = styled.p`
  margin: -8px 0 0;
  line-height: 1.6;
`

// the prototype's .path: the three programmes, the one the visitor is on tinted
const Programme = styled(Card)`
  ${(props) => props.$here && `background: ${ice2}; border-color: ${cyan};`}

  h3 {
    margin: 0 0 8px;
    font-size: 20px;
  }
`

// the prototype's drop-out chart: a row per stage, the day-30 bar under today's
const ROW = 64
const TOP = 76
const LEFT = 22

const DropOut = ({ transformation }) => {
  const { stages, bars, legend, figLabel } = transformation
  const height = TOP + ROW * stages.length + 12
  return (
    <Chart viewBox={`0 0 380 ${height}`} role="img" aria-label={figLabel}>
      <defs>
        <linearGradient id="focus-day30" x1="0" x2="1">
          <stop offset="0" stopColor={cyan} />
          <stop offset="1" stopColor={sky} />
        </linearGradient>
      </defs>
      <g fontWeight="600" fontSize="14">
        <rect x={LEFT} y="12" width="14" height="14" rx="4" fill="#6b6f77" />
        <text x="44" y="25" fill="#b9bcc2">
          {legend.today}
        </text>
        <rect x="204" y="12" width="14" height="14" rx="4" fill={cyan} />
        <text x="226" y="25" fill="#2ec0d6">
          {legend.day30}
        </text>
      </g>
      {stages.map((label, index) => {
        const y = TOP + ROW * index
        return (
          <g key={label}>
            <text x={LEFT} y={y} fontSize="16" fontWeight="700" fill="#ffffff">
              {label}
            </text>
            <rect x={LEFT} y={y + 10} width={bars.today[index]} height="14" rx="7" fill="#5c6068" />
            <rect x={LEFT} y={y + 30} width={bars.day30[index]} height="14" rx="7" fill="url(#focus-day30)" />
          </g>
        )
      })}
      <text x={LEFT} y={height - 10} fontSize="12" fill="#8c9097">
        {transformation.figNote}
      </text>
    </Chart>
  )
}

// each programme card reveals on its own; the Jumpstart card's two-part kicker carries the visitor's price
export const ProgrammeCard = ({ programme, price }) => (
  <Programme $here={programme.here} {...useRevealProps()}>
    <Kicker>
      {Array.isArray(programme.kicker)
        ? [programme.kicker[0], programme.here && price, programme.kicker[1]].filter(Boolean).join(' · ')
        : programme.kicker}
    </Kicker>
    <h3>{programme.title}</h3>
    <Bullets>
      {programme.items.map((item) => (
        <li key={item}>
          <RichText text={item} />
        </li>
      ))}
    </Bullets>
  </Programme>
)

/** "The transformation": where prospects or investors drop out today and by day 30, then the path after day 30. */
const TransformationSection = ({ offer }) => {
  const { transformation } = useFocus()
  const { after } = transformation
  const note = fillOffer(transformation.note[0], offer)

  return (
    <Section>
      <Wrap>
        <Stack $gap="clamp(24px, 3vw, 32px)">
          <SectionHead>
            <Eyebrow {...useRevealProps()}>{transformation.eyebrow}</Eyebrow>
            <H2 {...useRevealProps()}>
              {transformation.headline} <Hi>{transformation.headlineAccent}</Hi>
            </H2>
            <Lede {...useRevealProps()}>{transformation.lede}</Lede>
          </SectionHead>
          <Column $max={820} $gap={22}>
            <DarkPanel {...useRevealProps()}>
              <DotGrid aria-hidden="true" />
              <PanelInner>
                <Fig $raised>
                  <FigTitle>{transformation.figTitle}</FigTitle>
                  <DropOut transformation={transformation} />
                  <FigNote>
                    <b>{transformation.leaksLabel}</b> {transformation.leaks}{' '}
                    <b style={{ color: '#2ec0d6' }}>{transformation.pluggedLabel}</b> {transformation.plugged}
                  </FigNote>
                </Fig>
                <Closer>
                  {transformation.closer}
                  <em>{transformation.closerAccent}</em>
                </Closer>
              </PanelInner>
            </DarkPanel>
            <AfterTitle {...useRevealProps()}>{after.title}</AfterTitle>
            <Intro {...useRevealProps()}>{after.intro}</Intro>
            {after.programmes.map((programme) => (
              <ProgrammeCard key={programme.title} programme={programme} price={offer.price} />
            ))}
            <Small {...useRevealProps()}>{after.note}</Small>
            <div {...useRevealProps()}>
              <BuyButton $block>{transformation.cta}</BuyButton>
              <Note $center style={{ marginTop: 10 }}>
                {note}
                {transformation.note[1]}
              </Note>
            </div>
          </Column>
        </Stack>
      </Wrap>
    </Section>
  )
}

export default TransformationSection
